/**
 * Part list + slide-out edit panel (18.6).
 * Sub-list: vendor parts, BOM usage.
 */

import { useCallback, useEffect, useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import { fetchParts, createPart, updatePart, deletePart, restorePart, fetchVendorParts, createVendorPart, updateVendorPart, deleteVendorPart } from '../../api/parts';
import { fetchVendors } from '../../api/vendors';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination, SearchableSelect } from '../common';
import PanelDocuments from './PanelDocuments';

export default function PartList({ search, showDeleted }) {
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(fetchParts, { search, showDeleted });
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [vendorParts, setVendorParts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [addingVendor, setAddingVendor] = useState(false);
  const [newVendorForm, setNewVendorForm] = useState({ vendor: '', vendor_part_number: '' });
  const [vendorSaving, setVendorSaving] = useState(false);
  const [editingVpId, setEditingVpId] = useState(null);
  const [editingVpForm, setEditingVpForm] = useState({});
  const [vpSaving, setVpSaving] = useState(false);

  function openCreate() {
    setForm({ internal_part_no: '', description: '', unit_of_measure: 'each', cost: '', notes: '', send_documents_with_rfq: false });
    setCreating(true);
    setSelected(null);
    setVendorParts([]);
    setAddingVendor(false);
    setNewVendorForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
  }

  function openEdit(item) {
    setForm({
      internal_part_no: item.internal_part_no || '',
      description: item.description || '',
      unit_of_measure: item.unit_of_measure || 'each',
      cost: item.cost ?? '',
      notes: item.notes || '',
      send_documents_with_rfq: !!item.send_documents_with_rfq,
    });
    setSelected(item);
    setCreating(false);
    setAddingVendor(false);
    setNewVendorForm({ vendor: '', vendor_part_number: '' });
    loadVendorParts(item.id);
    loadVendors();
  }

  async function loadVendorParts(partId) {
    try {
      const resp = await fetchVendorParts({ part: partId, page_size: 50 });
      setVendorParts(resp.data || resp.results || resp || []);
    } catch {
      setVendorParts([]);
    }
  }

  async function loadVendors() {
    try {
      const resp = await fetchVendors({ page_size: 200 });
      setVendors(resp.data || resp.results || resp || []);
    } catch {
      setVendors([]);
    }
  }

  async function handleAddVendorPart() {
    if (!newVendorForm.vendor || !newVendorForm.vendor_part_number.trim()) {
      alert('Please select a vendor and enter a vendor part number.');
      return;
    }
    setVendorSaving(true);
    try {
      await createVendorPart({
        part: selected.id,
        vendor: newVendorForm.vendor,
        vendor_part_number: newVendorForm.vendor_part_number.trim(),
        is_approved: newVendorForm.is_approved ?? true,
        is_preferred: newVendorForm.is_preferred ?? false,
      });
      setNewVendorForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
      setAddingVendor(false);
      await loadVendorParts(selected.id);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to add vendor part');
    } finally {
      setVendorSaving(false);
    }
  }

  async function handleDeleteVendorPart(vpId) {
    if (!confirm('Remove this vendor from the part?')) return;
    try {
      await deleteVendorPart(vpId);
      if (editingVpId === vpId) setEditingVpId(null);
      await loadVendorParts(selected.id);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove vendor part');
    }
  }

  function openVpEdit(vp) {
    setEditingVpId(vp.id);
    setEditingVpForm({
      vendor_part_number: vp.vendor_part_number || '',
      product_url:        vp.product_url || '',
      is_approved:        vp.is_approved ?? false,
      is_preferred:       vp.is_preferred ?? false,
      current_unit_price: vp.current_unit_price ?? '',
      current_lead_time_days: vp.current_lead_time_days ?? '',
      price_multiplier_override: vp.price_multiplier_override ?? '',
      notes:              vp.notes || '',
      status:             vp.status || 'active',
    });
  }

  async function handleSaveVpEdit() {
    setVpSaving(true);
    try {
      await updateVendorPart(editingVpId, {
        vendor_part_number:     editingVpForm.vendor_part_number.trim(),
        product_url:            editingVpForm.product_url.trim(),
        is_approved:            editingVpForm.is_approved,
        is_preferred:           editingVpForm.is_preferred,
        current_unit_price:     editingVpForm.current_unit_price || null,
        current_lead_time_days: editingVpForm.current_lead_time_days || null,
        price_multiplier_override: editingVpForm.price_multiplier_override || null,
        notes:                  editingVpForm.notes,
        status:                 editingVpForm.status,
      });
      setEditingVpId(null);
      await loadVendorParts(selected.id);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setVpSaving(false);
    }
  }

  function closePanel() {
    setSelected(null);
    setCreating(false);
    setVendorParts([]);
    setAddingVendor(false);
    setNewVendorForm({ vendor: '', vendor_part_number: '' });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (creating) {
        const newPart = await createPart(form);
        // Switch to edit mode so the vendor section becomes available
        setCreating(false);
        setSelected(newPart);
        setAddingVendor(false);
        setNewVendorForm({ vendor: '', vendor_part_number: '', is_approved: true, is_preferred: false });
        await loadVendorParts(newPart.id);
        await loadVendors();
      } else {
        await updatePart(selected.id, form);
        closePanel();
      }
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this part?')) return;
    try { await deletePart(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  }

  async function handleRestore() {
    try { await restorePart(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Restore failed'); }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="px-4 py-2 border-b border-gray-100">
        <Button size="sm" onClick={openCreate}>+ New Part</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No parts" description="Add parts to build your parts catalog." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Part Number</th>
                <th className="px-4 py-2">Ref Part #</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Preferred Vendor</th>
                <th className="px-4 py-2 text-center">UOM</th>
                <th className="px-4 py-2 text-center">Vendors</th>
                <th className="px-4 py-2 text-center">Review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`hover:bg-blue-50 cursor-pointer ${item.deleted_at ? 'opacity-50 line-through' : ''}`}
                >
                  <td className="px-4 py-2 font-mono text-xs font-medium text-foreground">{item.internal_part_no}</td>
                  <td className="px-4 py-2 font-mono text-xs text-blue-600">{item.reference_part_number || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 truncate max-w-[200px]">{item.description || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 truncate max-w-[200px]">{item.preferred_vendor_display || '—'}</td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.unit_of_measure || '—'}</td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.vendor_parts_count ?? '—'}</td>
                  <td className="px-4 py-2 text-center">
                    {item.needs_review && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">Review</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New Part' : `Edit: ${selected?.internal_part_no || ''}`}
        width="max-w-lg"
      >
        <div className="space-y-4">
          <TextInput label="Internal Part Number" value={form.internal_part_no} onChange={(v) => setForm({ ...form, internal_part_no: v })} />
          <TextInput label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.unit_of_measure || 'each'}
              onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
            >
              <option value="each">Each</option>
              <option value="ft">Feet</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost ($)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Unit cost (optional)"
              value={form.cost ?? ''}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
            />
          </div>
          <TextInput label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} multiline />

          {/* RFQ document attachment toggle */}
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={form.send_documents_with_rfq || false}
              onChange={(e) => setForm({ ...form, send_documents_with_rfq: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 mt-0.5"
            />
            <div>
              <span className="font-medium">Send documents with RFQ</span>
              <p className="text-xs text-gray-400 mt-0.5">
                Attach this part's documents (spec sheets, drawings, etc.) to RFQ emails.
              </p>
            </div>
          </label>

          {/* Approved Vendors — interactive add/delete */}
          {selected && (
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">
                  Approved Vendors ({vendorParts.length})
                </h4>
                <button
                  onClick={() => { setAddingVendor(true); setNewVendorForm({ vendor: '', vendor_part_number: '' }); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Vendor
                </button>
              </div>

              {/* Existing vendor parts */}
              {vendorParts.length === 0 && !addingVendor && (
                <p className="text-xs text-gray-400 italic">No approved vendors. Click + Add Vendor to add one.</p>
              )}
              <div className="space-y-1.5 mb-2">
                {vendorParts.map((vp) => (
                  <div key={vp.id} className="border border-gray-200 rounded overflow-hidden">
                    {/* Summary row */}
                    <div className="flex items-center justify-between bg-gray-50 px-2.5 py-1.5">
                      <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-gray-800">{vp.vendor_name || vp.vendor}</span>
                        {vp.is_preferred && (
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">Preferred</span>
                        )}
                        {!vp.is_approved && (
                          <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">Not Approved</span>
                        )}
                        <span className="text-gray-400">·</span>
                        <span className="text-xs font-mono text-gray-600">{vp.vendor_part_number || <em className="text-gray-400">no VPN</em>}</span>
                        {vp.current_list_price && vp.uses_list_pricing && (
                          <span className="text-xs text-gray-400">List ${vp.current_list_price}</span>
                        )}
                        {vp.current_unit_price && (
                          <span className="text-xs text-gray-400">${vp.current_unit_price}</span>
                        )}
                        {vp.current_lead_time_days ? (
                          <span className="text-xs text-gray-400">{vp.current_lead_time_days}d lead</span>
                        ) : null}
                        {vp.effective_multiplier && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded font-medium">×{vp.effective_multiplier}</span>
                        )}
                        {vp.product_url && (
                          <a
                            href={vp.product_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-500 hover:text-blue-700 underline"
                          >link</a>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <button
                          onClick={() => editingVpId === vp.id ? setEditingVpId(null) : openVpEdit(vp)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1.5 py-0.5 rounded hover:bg-blue-50"
                          title="Edit vendor part details"
                        >
                          {editingVpId === vp.id ? 'Close' : 'Edit'}
                        </button>
                        <button
                          onClick={() => handleDeleteVendorPart(vp.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Remove vendor"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Inline edit form */}
                    {editingVpId === vp.id && (
                      <div className="bg-white border-t border-gray-200 px-3 py-3 space-y-2.5">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Vendor Part Number</label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                              value={editingVpForm.vendor_part_number}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, vendor_part_number: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Status</label>
                            <select
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              value={editingVpForm.status}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, status: e.target.value })}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Product URL</label>
                          <input
                            type="url"
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                            placeholder="https://vendor.com/product/..."
                            value={editingVpForm.product_url}
                            onChange={(e) => setEditingVpForm({ ...editingVpForm, product_url: e.target.value })}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Unit Price ($)</label>
                            <input
                              type="number"
                              step="0.0001"
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              placeholder="0.00"
                              value={editingVpForm.current_unit_price}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, current_unit_price: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Lead Time (days)</label>
                            <input
                              type="number"
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              placeholder="e.g. 7"
                              value={editingVpForm.current_lead_time_days}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, current_lead_time_days: e.target.value })}
                            />
                          </div>
                        </div>

                        {/* Multiplier override — only show for list-price vendors */}
                        {vp.uses_list_pricing && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Price Multiplier Override</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm"
                                placeholder={vp.effective_multiplier ? String(vp.effective_multiplier) : 'vendor default'}
                                value={editingVpForm.price_multiplier_override}
                                onChange={(e) => setEditingVpForm({ ...editingVpForm, price_multiplier_override: e.target.value })}
                              />
                              <span className="text-xs text-gray-400">Leave blank to use vendor default</span>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingVpForm.is_approved}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, is_approved: e.target.checked })}
                            />
                            <span className="text-xs text-gray-700">Approved</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingVpForm.is_preferred}
                              onChange={(e) => setEditingVpForm({ ...editingVpForm, is_preferred: e.target.checked })}
                            />
                            <span className="text-xs text-gray-700">Preferred</span>
                          </label>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
                          <textarea
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                            rows={2}
                            value={editingVpForm.notes}
                            onChange={(e) => setEditingVpForm({ ...editingVpForm, notes: e.target.value })}
                          />
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <Button size="sm" loading={vpSaving} onClick={handleSaveVpEdit}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingVpId(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add vendor form */}
              {addingVendor && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2.5 space-y-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Vendor</label>
                    <SearchableSelect
                      value={newVendorForm.vendor}
                      onChange={(v) => setNewVendorForm({ ...newVendorForm, vendor: v })}
                      options={vendors.map((v) => ({ value: v.id, label: v.vendor_name }))}
                      placeholder="Select vendor…"
                      emptyMessage="No vendors match"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Vendor Part Number</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                      placeholder="e.g. ABC-12345"
                      value={newVendorForm.vendor_part_number}
                      onChange={(e) => setNewVendorForm({ ...newVendorForm, vendor_part_number: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newVendorForm.is_approved ?? true}
                        onChange={(e) => setNewVendorForm({ ...newVendorForm, is_approved: e.target.checked })}
                      />
                      <span className="text-xs text-gray-700">Approved</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newVendorForm.is_preferred ?? false}
                        onChange={(e) => setNewVendorForm({ ...newVendorForm, is_preferred: e.target.checked })}
                      />
                      <span className="text-xs text-gray-700">Preferred</span>
                    </label>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" loading={vendorSaving} onClick={handleAddVendorPart}>Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingVendor(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>Save</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
            {selected && !selected.deleted_at && (
              <Button variant="danger" size="sm" className="ml-auto" onClick={handleDelete}>Delete</Button>
            )}
            {selected?.deleted_at && (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleRestore}>Restore</Button>
            )}
          </div>

          {selected && <PanelDocuments entityType="part" entityId={selected.id} />}
        </div>
      </SlideOutPanel>
    </div>
  );
}

function TextInput({ label, value, onChange, type = 'text', multiline = false }) {
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea className={cls} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
